import {
    DoCheck,
    ElementRef,
    HostBinding,
    Input,
    OnChanges,
    Optional,
    Renderer2,
    Self,
    SimpleChanges,
    OnInit,
    HostListener,
    ChangeDetectorRef,
    Component,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { InputDirective } from '../../input.directive';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'input[euiInputCheckBox]',
    styleUrls: ['./styles/_index.scss'],
    template: '',
    encapsulation: ViewEncapsulation.None
})
export class EuiInputCheckboxComponent extends InputDirective implements OnInit, DoCheck, OnChanges, ControlValueAccessor {

    @HostBinding('class')
    public get cssClasses(): any {
        return [
            this.getCssClasses('eui-input-checkbox'),
            this._hasLabel ? '' : 'eui-input-checkbox--nolabel',
            this._isInvalid ? `eui-input-checkbox--invalid` : '',
        ].join(' ').trim();
    }

    @Input()
    public get isInvalid() {
        return this._isInvalid || null;
    }
    public set isInvalid(state: any) {
        this.setInvalid(state);
    }
    protected _isInvalid: boolean;
    @Input() label: string;
    protected _labelRef: HTMLLabelElement;

    @Input() styleClass: string;
    @HostBinding('attr.type') protected type: string = 'checkbox';
    @HostBinding('attr.checked')
    @Input()
    public get checked(): boolean {
        return this._checked;
    }
    public set checked(value: boolean) {
        this._checked = coerceBooleanProperty(value) ? true : undefined;
    }
    protected _checked: boolean;

    @HostBinding('attr.id')
    @Input()
    public get id(): string {
        return this._id;
    }
    public set id(value: string|null) {
        this.setIdAttribute(value);
    }
    protected _id: string;
    private _hasLabel: boolean;

    constructor(
        @Optional() @Self() protected ngControl: NgControl,
        protected _elementRef: ElementRef<HTMLInputElement>,
        protected _renderer: Renderer2,
        private _cd: ChangeDetectorRef,
    ) {
        super(_elementRef, _renderer, ngControl);

        // if there's no id attribute set one
        if (!this._id) {
            this.setIdAttribute();
        }

        // register control valueAccessor
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit() {
        super.ngOnInit();

        // in case control value is null set the default one (isChecked) and sync Control State
        if (this.ngControl?.control?.value === null) {
            this.ngControl.control.setValue(this._checked, { emitModelToViewChange: false });
            // changing Model Expression after view checked, so detect changes
            // TODO: check why although it's checked .checked returns false
            // this.ngControl.viewToModelUpdate(this._checked);
            // this._cd.detectChanges();
        }
    }

    ngDoCheck() {
        this._hasLabel = document.querySelector(`[for='${this._id}']`) ? true : false;
        if (this.ngControl) {
            this._isInvalid = this.ngControl.invalid && this.ngControl.touched;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['label']) {
            // in case label Element already created update the value
            if (this._labelRef) {
                this._renderer.setProperty(this._labelRef, 'innerText', changes['label'].currentValue);
            } else {
                // create label Element
                this._labelRef = this._renderer.createElement('label');
                this._renderer.setProperty(this._labelRef, 'innerText', changes['label'].currentValue);
                this._renderer.setAttribute(this._labelRef, 'for', this._id);
                // append label element under the Checkbox
                const parent: HTMLElement = this._elementRef.nativeElement.parentElement;
                this._renderer.appendChild(parent, this._labelRef);
                this._renderer.insertBefore(parent, this._labelRef, this._elementRef.nativeElement.nextSibling);
            }
        }

        // in case id change then sync label for attribute
        if (changes['id'] && this._labelRef) {
            this._renderer.setAttribute(this._labelRef, 'for', changes['id'].currentValue);
        }
    }

    writeValue(obj: boolean): void {
        this._checked = coerceBooleanProperty(obj) ? true : undefined;
        this._elementRef.nativeElement.checked = this._checked ? true : undefined;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onBlur = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    @HostListener('change', ['$event.target.checked'])
    protected onChanged(checked) {
        this.onChange(checked);
    }

    protected onChange = (_) => { /* Nothing to be Done so far */ };
    protected onBlur = (_) => { /* Nothing to be Done so far */ };

    protected setInvalid(state?: any): void {
        // in case it's controlled by NgControl override
        this._isInvalid = this.control ? this.control.invalid && this.control.touched : coerceBooleanProperty(state);
    }
}
